/**
 * Eksportene under «control-room» i katalogen (docs/EKSPORT.md §9). API-halvdelen:
 * `apps/api/src/export/definitions/control-room/`.
 *
 * BARE FOR EN PLATTFORMADMIN, OG BARE DET CONTROL ROOM-SIDENE VISER. Plattformadmin er ingen rolle i
 * en organisasjon og ingen celle i Rettigheter, så `requires` kan ikke si det: definisjonene spør den
 * samme vakten som rutene (`PlatformAdminGuard`, `app.is_platform_admin()` i basen), og alle andre
 * får 404 som på `/v1/platform/*`. Dataene hentes gjennom de samme tjenestene og plattformfunksjonene
 * som sidene — tall og navngitte felt, aldri kundens innhold.
 *
 * Id-ene er `control_room.*` fordi id-mønsteret (`createExportRequestSchema`) ikke tar bindestrek i
 * området. Tekstene står under `export.controlRoom.*`.
 */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

const col = (
  exportName: string,
  key: string,
  type: ExportColumnMeta['type'],
  defaultHidden = false,
): ExportColumnMeta => ({
  key,
  labelKey: `export.controlRoom.${exportName}.columns.${key}`,
  type,
  ...(defaultHidden ? { defaultHidden: true } : {}),
});

/**
 * ORGANISASJONER MED BRUK OG ABONNEMENT: Control Room › Organisasjoner, med søket, «Arkiverte» og
 * sorteringen (`ListControlRoomOrganizationsQuery` uten side) og kolonnene brukeren har valgt der
 * (`tableKey: 'control-room-organizations'`). Abonnementet (plan, status, notat) er det
 * organisasjonens side viser under «Frysing og abonnement». Plattformens egen organisasjon er aldri
 * med, som i lista.
 */
const ORGANIZATIONS: ExportMeta = {
  id: 'control_room.organizations',
  domain: 'control_room',
  kind: 'table',
  titleKey: 'export.controlRoom.organizations.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: [],
  guest: false,
  tableKey: 'control-room-organizations',
  landscape: true,
  columns: [
    col('organizations', 'name', 'text'),
    col('organizations', 'kind', 'status'),
    col('organizations', 'status', 'status', true),
    col('organizations', 'superadmin', 'text'),
    col('organizations', 'superadminEmail', 'email'),
    col('organizations', 'members', 'integer'),
    col('organizations', 'externals', 'integer'),
    col('organizations', 'linkPeople', 'integer', true),
    col('organizations', 'editions', 'integer'),
    col('organizations', 'concerts', 'integer'),
    col('organizations', 'lastActiveOn', 'date'),
    col('organizations', 'storage', 'decimal', true),
    col('organizations', 'createdAt', 'date'),
    col('organizations', 'plan', 'text'),
    col('organizations', 'subscriptionStatus', 'status'),
    col('organizations', 'subscriptionNote', 'multiline', true),
    col('organizations', 'subscriptionUpdatedAt', 'date', true),
  ],
};

/**
 * ÉN ORGANISASJON (utover EKSPORT.md §9): siden per organisasjon i Control Room som dokument —
 * tallene, delene av Tvitvi de bruker, antall, aktive 7/30, abonnementet og brukerlista. Å åpne siden
 * logger `organization.viewed` i plattformloggen, og det gjør eksporten også (samme tjeneste).
 */
const ORGANIZATION: ExportMeta = {
  id: 'control_room.organization',
  domain: 'control_room',
  kind: 'document',
  titleKey: 'export.controlRoom.organization.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object'],
  requires: [],
  guest: false,
  sections: [
    { key: 'figures', labelKey: 'export.controlRoom.organization.sections.figures' },
    { key: 'usage', labelKey: 'export.controlRoom.organization.sections.usage' },
    { key: 'subscription', labelKey: 'export.controlRoom.organization.sections.subscription' },
    { key: 'users', labelKey: 'export.controlRoom.organization.sections.users' },
  ],
};

/** PLATTFORMLOGGEN: Control Room › Logg, alt plattformadminene har gjort, nyeste først. */
const PLATFORM_LOG: ExportMeta = {
  id: 'control_room.platform-log',
  domain: 'control_room',
  kind: 'table',
  titleKey: 'export.controlRoom.platformLog.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: [],
  guest: false,
  landscape: true,
  columns: [
    col('platformLog', 'when', 'datetime'),
    col('platformLog', 'who', 'email'),
    col('platformLog', 'what', 'text'),
    col('platformLog', 'organization', 'text'),
    col('platformLog', 'details', 'multiline'),
  ],
};

/**
 * LOGGEN OVER BYRÅSKANNINGER: Control Room › Byråskanning › Logg, én rad per kjøring med tallene.
 * Bare tall, aldri artistnavn — som siden.
 */
const SCAN_LOG: ExportMeta = {
  id: 'control_room.scan-log',
  domain: 'control_room',
  kind: 'table',
  titleKey: 'export.controlRoom.scanLog.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: [],
  guest: false,
  landscape: true,
  columns: [
    col('scanLog', 'started', 'datetime'),
    col('scanLog', 'trigger', 'text'),
    col('scanLog', 'status', 'status'),
    col('scanLog', 'finished', 'datetime', true),
    col('scanLog', 'organizations', 'integer'),
    col('scanLog', 'agencies', 'integer'),
    col('scanLog', 'in', 'integer'),
    col('scanLog', 'out', 'integer'),
    col('scanLog', 'switched', 'integer'),
    col('scanLog', 'review', 'integer'),
    col('scanLog', 'errors', 'integer'),
  ],
};

/**
 * ÉN SKANNING (utover EKSPORT.md §9): kjøringen per organisasjon, som vinduet som åpnes fra loggen.
 * Tallene og organisasjonens navn — aldri hvem.
 */
const SCAN_RUN: ExportMeta = {
  id: 'control_room.scan-run',
  domain: 'control_room',
  kind: 'table',
  titleKey: 'export.controlRoom.scanRun.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object'],
  requires: [],
  guest: false,
  landscape: true,
  columns: [
    col('scanRun', 'organization', 'text'),
    col('scanRun', 'result', 'status'),
    col('scanRun', 'agencies', 'integer'),
    col('scanRun', 'unreadable', 'integer'),
    col('scanRun', 'in', 'integer'),
    col('scanRun', 'out', 'integer'),
    col('scanRun', 'switched', 'integer'),
    col('scanRun', 'agentChanges', 'integer'),
    col('scanRun', 'newArtists', 'integer'),
    col('scanRun', 'newAgencies', 'integer', true),
    col('scanRun', 'review', 'integer'),
  ],
};

/**
 * DEN OFFISIELLE ARTISTLISTA: Control Room › Offisiell liste › Artister, med søket, byrået og
 * sorteringen (`ListOfficialArtistsQuery` uten side) og kolonnene brukeren har valgt der
 * (`tableKey: 'control-room-official-artists'`). De skjulte kolonnene er feltene panelet viser.
 */
const OFFICIAL_ARTISTS: ExportMeta = {
  id: 'control_room.official-artists',
  domain: 'control_room',
  kind: 'table',
  titleKey: 'export.controlRoom.officialArtists.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: [],
  guest: false,
  tableKey: 'control-room-official-artists',
  landscape: true,
  columns: [
    col('officialArtists', 'name', 'text'),
    col('officialArtists', 'agency', 'text'),
    col('officialArtists', 'genre', 'text'),
    col('officialArtists', 'artistType', 'text', true),
    col('officialArtists', 'homeTown', 'text'),
    col('officialArtists', 'gender', 'status', true),
    col('officialArtists', 'listeners', 'integer'),
    col('officialArtists', 'instagramFollowers', 'integer', true),
    col('officialArtists', 'partySize', 'integer', true),
    col('officialArtists', 'scanDiscoveredAt', 'date', true),
    col('officialArtists', 'updated', 'date'),
    col('officialArtists', 'frontPerson', 'text', true),
    col('officialArtists', 'ageGroup', 'text', true),
    col('officialArtists', 'identity', 'status', true),
    col('officialArtists', 'spotifyUrl', 'text', true),
    col('officialArtists', 'instagramHandle', 'text', true),
  ],
};

/** BYRÅENE I DEN OFFISIELLE LISTA: Control Room › Offisiell liste › Byråer. */
const OFFICIAL_AGENCIES: ExportMeta = {
  id: 'control_room.official-agencies',
  domain: 'control_room',
  kind: 'table',
  titleKey: 'export.controlRoom.officialAgencies.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: [],
  guest: false,
  columns: [
    col('officialAgencies', 'name', 'text'),
    col('officialAgencies', 'artists', 'integer'),
    col('officialAgencies', 'website', 'text'),
    col('officialAgencies', 'artistPage', 'text'),
  ],
};

export const CONTROL_ROOM_EXPORTS: readonly ExportMeta[] = [
  ORGANIZATIONS,
  ORGANIZATION,
  PLATFORM_LOG,
  SCAN_LOG,
  SCAN_RUN,
  OFFICIAL_ARTISTS,
  OFFICIAL_AGENCIES,
];
